import { materializeFile } from "common/artwork-file-types";
import { api } from "jiffy-api";
import { redirectTo } from "common/utils/browser";
import { isFileTypeSupported } from "common/file-type-helper";

export default class DtfFileProcessor {
  constructor($container, uiInstance, uploadManagerInstance) {
    this.$container = $container;
    this.tileId = $container.dataset.tileId;
    this.catalogNumber = $container.dataset.catalogNumber || "DTF001";
    this.uiInstance = uiInstance;
    this.uploadManager = uploadManagerInstance;
    this.uploadedFile = null;
  }

  handleFileSelection(file) {
    if (this.uploadManager.isUploadActive(this.tileId)) {
      return false;
    }

    const { valid, message } = isFileTypeSupported(file, false);

    if (!valid) {
      this.uiInstance.setErrorMessage(message);
      return false;
    }

    this.uploadedFile = file;
    return true;
  }

  #createFormData() {
    const formData = new FormData();
    formData.append("generate_link", true);
    formData.append("file", this.uploadedFile);
    formData.append("catalog_number", this.catalogNumber);
    formData.append("is_made", false);
    formData.append("is_made_flash", false);
    return formData;
  }

  #processError(e) {
    const errorResponse = e.response?.data;
    const errorMessage = errorResponse?.error || e.message;
    let uploadError = errorMessage;
    if (errorMessage === "Network Error") {
      uploadError = this.uiInstance.percentage === 100 ? "Processing error" : "Upload error";
    }

    this.uiInstance.hideProgress();
    this.uiInstance.setErrorMessage(uploadError);
  }

  // eslint-disable-next-line max-lines-per-function
  async uploadArtwork() {
    if (!this.uploadedFile) return;

    // Show progress UI immediately with "Preparing..." status
    this.uploadManager.setActiveUpload(this.tileId);
    this.uiInstance.showProgress(this.uploadedFile.name);
    this.uiInstance.updateProgress(0, "Preparing file...");

    // Materialize the file to handle cloud storage providers (Google Drive, Dropbox) on mobile
    const { file: materializedFile, error: materializeError } = await materializeFile(
      this.uploadedFile,
      {
        timeoutMs: 120000, // 2 minute timeout for large files from cloud storage
        onProgress: progress => {
          // Show preparation progress (0-10% range)
          const prepProgress = Math.round(progress * 10);
          this.uiInstance.updateProgress(prepProgress, "Preparing file...");
        }
      }
    );

    if (materializeError || !materializedFile) {
      this.uiInstance.hideProgress();
      this.uiInstance.setErrorMessage(materializeError || "Failed to read file. Please try again.");
      this.uploadManager.clearActiveUpload();
      return;
    }
    this.uploadedFile = materializedFile;

    // Update to uploading status
    this.uiInstance.updateProgress(10, "Uploading...");

    try {
      const response = await api.dtf.uploadArtwork(this.#createFormData(), {
        raw: true,
        headers: { "Content-Type": undefined },
        useAxiosForRequest: true,
        onUploadProgress: ({ progress, loaded, total }) => {
          // Calculate upload progress (10-95% range, reserving last 5% for server processing)
          const uploadProgress = 10 + Math.round((progress || loaded / total || 0) * 85);
          this.uiInstance.updateProgress(uploadProgress, "Uploading...");
        }
      });

      this.uiInstance.updateProgress(100, "Redirecting...");
      setTimeout(() => redirectTo(response.redirect_url), 500);
    } catch (e) {
      this.#processError(e);
      this.uploadManager.clearActiveUpload();
    }
  }
}
